type MetaFields = Record<string, string | boolean | null>;

type EventCallbacks = Record<string, (data: unknown) => void>;

type AdaStartSettings = {
  handle: string;
  language: string;
  eventCallbacks: EventCallbacks;
};

type AdaEmbed = {
  setMetaFields: (fields: MetaFields) => Promise<void>;
  setSensitiveMetaFields: (fields: MetaFields) => Promise<void>;
  start: (settings: AdaStartSettings) => Promise<void>;
  toggle: () => Promise<void>;
};

// https://docs.ada.cx/messaging/web/getting-started
const sdkUrl = 'https://messaging-assets.ada.support/sdk.js';

let adaEmbed: AdaEmbed | undefined;
let startPromise: Promise<void> | undefined;
let latestEventCallbacks: EventCallbacks = {};
let isRolledOut = false;

export function getAdaEmbed() {
  return adaEmbed;
}

export function isAdaRolledOut() {
  return isRolledOut;
}

// Starts once per page; later calls only swap in their event callbacks. Returns a function that unregisters them.
export function startAdaEmbed({ eventCallbacks, ...settings }: AdaStartSettings) {
  latestEventCallbacks = eventCallbacks;

  startPromise ??= (async () => {
    const { createAdaEmbedInterface } = await import(/* webpackIgnore: true */ sdkUrl);
    adaEmbed = createAdaEmbedInterface() as AdaEmbed;
    await adaEmbed.start({
      ...settings,
      eventCallbacks: Object.fromEntries(
        // Only the first caller's event names reach the SDK.
        Object.keys(eventCallbacks).map(name => [
          name,
          (data: unknown) => {
            if (name === 'ada:start') {
              isRolledOut = (data as { isRolledOut: boolean }).isRolledOut;
            }
            latestEventCallbacks[name]?.(data);
          }
        ])
      )
    });
  })().catch(error => {
    // Ad blockers commonly block the Ada SDK, so this isn't reported as an error.
    console.warn('Ada SDK failed to start', error);
  });

  return () => {
    if (latestEventCallbacks === eventCallbacks) {
      latestEventCallbacks = {};
    }
  };
}
